import { useMemo } from 'react';
import { createHook, type BoundActions, createSelector } from 'react-sweet-state';
import { ff } from '@atlassian/jira-feature-flagging';
import type {
	State,
	ThirdPartyFieldConfigStateForItem,
	ThirdPartyFieldConfigState,
} from '../../../../common/types';
import { useFieldMappingItemTypeContext } from '../../../../common/utils/field-mapping-item-type-context';
import type { Actions } from '../../actions';
import type { SetIsThirdPartyItemTypeFieldIncludedAction } from '../../actions/set-is-third-party-field-included';
import type { SetThirdPartyItemTypeFieldMappingAction } from '../../actions/set-third-party-field-mapping';
import { Store } from '../../main';

const thirdPartyFieldConfigSelector = (state: State): ThirdPartyFieldConfigState | null =>
	state.thirdPartyState.thirdPartyFieldConfigState;

export type ThirdPartyFieldConfigActions = {
	setIsThirdPartyFieldIncluded: SetIsThirdPartyItemTypeFieldIncludedAction;
	setThirdPartyFieldMapping: SetThirdPartyItemTypeFieldMappingAction;
};

export type UseThirdPartyFieldConfigState = () => [
	ThirdPartyFieldConfigStateForItem | null,
	BoundActions<State, ThirdPartyFieldConfigActions>,
];

export const thirdPartyFieldConfigStateSelector = createSelector(
	thirdPartyFieldConfigSelector,
	(_, props: { itemType: string | null }): string | null => props && props.itemType,
	(newState, itemType): ThirdPartyFieldConfigStateForItem | null => {
		if (newState == null || itemType == null) return null;

		return {
			...newState.fieldMappingState[itemType],
			availableJiraFields: newState.availableJiraFields,
		};
	},
);

export const useThirdPartyFieldConfigStateInner = createHook(Store, {
	selector: thirdPartyFieldConfigStateSelector,
});

export const useThirdPartyFieldConfigState: UseThirdPartyFieldConfigState = () => {
	const itemType = useFieldMappingItemTypeContext();
	const [state, { setIsThirdPartyFieldIncluded, setThirdPartyFieldMapping }] =
		useThirdPartyFieldConfigStateInner({ itemType });

	const actions = useMemo(
		() => ({
			setIsThirdPartyFieldIncluded: setIsThirdPartyFieldIncluded.bind(null, itemType),
			setThirdPartyFieldMapping: setThirdPartyFieldMapping.bind(null, itemType),
		}),
		[itemType, setIsThirdPartyFieldIncluded, setThirdPartyFieldMapping],
	);

	return [state, actions];
};

export type ThirdPartyFieldsIncludedState = {
	numFieldsIncluded: number;
	numThirdPartyFields: number;
	hasUnsupportedFieldFiltered?: boolean;
};

export type UseThirdPartyFieldsIncludedState = () => [
	ThirdPartyFieldsIncludedState | null,
	Partial<BoundActions<State, Actions>>,
];

export const thirdPartyFieldsIncludedStateSelector = createSelector(
	thirdPartyFieldConfigSelector,
	(newState: ThirdPartyFieldConfigState | null) => {
		if (newState === null) {
			return {
				numFieldsIncluded: 0,
				numThirdPartyFields: 0,
			};
		}
		const { fieldMappingState, hasUnsupportedFieldFiltered } = newState;
		let numThirdPartyFields = 0;
		let numFieldsIncluded = 0;
		Object.keys(fieldMappingState).forEach((key) => {
			const fieldMapping = fieldMappingState[key];
			numThirdPartyFields += fieldMapping.thirdPartyFieldOrder.length;
			numFieldsIncluded += Object.values(fieldMapping?.thirdPartyFieldMappings || {}).filter(
				(field) =>
					ff('jwm-x2c-hierarchy-mapping_kkjnd')
						? fieldMapping.thirdPartyFieldOrder.includes(field.thirdPartyField.fieldId) &&
							field.mappingContext.isIncluded
						: field.mappingContext.isIncluded,
			).length;
		});

		return {
			numThirdPartyFields,
			numFieldsIncluded,
			hasUnsupportedFieldFiltered,
		};
	},
);

export const useThirdPartyFieldsIncludedState = createHook(Store, {
	selector: thirdPartyFieldsIncludedStateSelector,
});

export const thirdPartyFieldsItemTypesSelector = createSelector(
	thirdPartyFieldConfigSelector,
	(newState: ThirdPartyFieldConfigState | null) =>
		newState ? Object.keys(newState.fieldMappingState) : [],
);

export type UseThirdPartyFieldsItemTypes = () => [string[], Partial<BoundActions<State, Actions>>];

export const useThirdPartyFieldsItemTypes = createHook(Store, {
	selector: thirdPartyFieldsItemTypesSelector,
});
